import { useEffect, useState } from "react";
import cloneDeep from "lodash/cloneDeep";
import { useEmployeeStream } from "../../hooks/EmployeStream";
import { CircleX, CrossIcon, RemoveFormattingIcon } from "lucide-react";

export default function EmployeeUpdateRequest() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { updateEmployee } = useEmployeeStream(`${process.env.REACT_APP_PUBLIC_API_BASE_URL}/v1/employees/employees/stream`);

  const fetchApprovals = async () => {
    try {
      setLoading(true);
      const res = await fetch(
        `${process.env.REACT_APP_PUBLIC_API_BASE_URL}/v1/employees/approvals-requests`,
        {
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
        }
      );
      if (res.status === 401) {
        localStorage.removeItem("access_token");
        window.location.href = "/login";
      }
      if (!res.ok) throw new Error(res.statusText);
      const data = await res.json();
      // Deep clone to allow local edits
      setRequests(
        data.map((req) => ({ ...req, editedData: cloneDeep(req.data) }))
      );
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchApprovals();
  }, []);

  const handleInputChange = (reqId, fieldPath, value) => {
    setRequests((prev) =>
      prev.map((r) => {
        if (r._id !== reqId) return r;
        // Update nested field using path (like personal_information.employee_name)
        const updatedData = cloneDeep(r.editedData);
        const updateFieldsValue = r.fieldsUpdated;
        updateFieldsValue[fieldPath] = {...updateFieldsValue[fieldPath], newValue: value};
        const keys = fieldPath.split(".");
        let obj = updatedData;
        for (let i = 0; i < keys.length - 1; i++) obj = obj[keys[i]];
        obj[keys[keys.length - 1]] = value;
        return { ...r, editedData: updatedData };
      })
    );
  };

  const handleApprove = async (req) => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_PUBLIC_API_BASE_URL}/v1/employees/${req.eid}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
          credentials: "include",
          body: JSON.stringify(req.editedData),
        }
      );
      if (!res.ok) throw new Error("Failed to approve request");
      updateEmployee(req.editedData);

      await fetch(
        `${process.env.REACT_APP_PUBLIC_API_BASE_URL}/v1/employees/pending-approval/${req._id}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
          credentials: "include",
        }
      );
      setRequests((prev) => prev.filter((r) => r._id !== req._id));
    } catch (err) {
      alert(err.message);
    }
  };

  const handleReject = async (id) => {
    try {
      await fetch(
        `${process.env.REACT_APP_PUBLIC_API_BASE_URL}/v1/employees/pending-approval/${id}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
          credentials: "include",
        }
      );
      setRequests((prev) => prev.filter((r) => r._id !== id));
    } catch (err) {
      alert(err.message);
    }
  };

  if (loading) return <p className="p-6 text-gray-600">Loading...</p>;
  if (error) return <p className="p-6 text-red-500">{error}</p>;
  if (!requests.length)
    return <p className="p-6 text-gray-600">No pending approval requests 🎉</p>;

  return (
    <div className="p-6 bg-gray-100 min-h-screen">
      <h1 className="text-2xl font-bold mb-6 text-gray-800">
        Approval Requests
      </h1>

      {requests.map((req) => (
        <div
          key={req._id}
          className="bg-white shadow-md rounded-xl p-4 border border-gray-200 hover:shadow-lg transition mb-6"
        >
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-gray-700">
              Employee ID: {req.eid}
            </h2>
            <h1>{req.editedData.personal_information.employee_name}</h1>
            <div className="flex gap-2">
              <button
                onClick={() => handleApprove(req)}
                className="px-4 py-2 bg-green-600 text-white rounded-lg shadow hover:bg-green-700 transition"
              >
                Approve
              </button>
              <button
                onClick={() => handleReject(req._id)}
                className="px-4 py-2 bg-red-600 text-white rounded-lg shadow hover:bg-red-700 transition"
              >
                Reject
              </button>
            </div>
          </div>

          <table className="table-auto w-full border border-gray-200 text-sm">
            <thead>
              <tr className="bg-gray-100">
                <th className="border px-2 py-1">Field</th>
                <th className="border px-2 py-1">Old Value</th>
                <th className="border px-2 py-1">New Value (Editable)</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(req.fieldsUpdated).map(
                ([field, { oldValue, newValue }]) => oldValue==newValue ? null : (
                  <tr key={field}>
                    <td className="border px-2 py-1 font-medium">{field}</td>
                    <td className="border px-2 py-1">
                      {Array.isArray(oldValue) ? oldValue.join(", ") : oldValue}
                    </td>
                    <td className="border px-2 py-1 flex items-center gap-2">
                      <input
                        type="text"
                        className="w-full border px-1 py-0.5 rounded"
                        value={
                          Array.isArray(newValue)
                            ? newValue.join(", ")
                            : newValue
                        }
                        onChange={(e) =>
                          handleInputChange(req._id, field, e.target.value)
                        }
                      />
                      <button
                        onClick={() => handleInputChange(req._id, field, Array.isArray(oldValue) ? oldValue.join(", ") : oldValue)}
                        className="text-red-500 rounded-lg shadow hover:text-red-700 transition"
                      >
                       <CircleX />
                      </button>
                    </td>
                  </tr>
                )
              )}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}
